// Native
import { useParams } from "react-router"
import { Link } from "react-router-dom"

// Style
import { Avatar, Button, ButtonGroup, Divider, Flex, Grid, GridItem, Stack, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from "@chakra-ui/react"
import { Alert, Container, Icon, Label, IconButton, Clipboard, Breadcrumb, Dash, Dev, Menu, Scope } from "components"

// Config
import {Mask, Moment, Functions, API} from "config"

// Hooks
import { useDistributor, useUser } from "hooks"

// Screens
import Stock from "./stock"
import Orders from "./orders"
import Shopping from "./shopping"
import Extract from "./extract"
import Block from "./block"
import Edit from "./edit"
import Settings from "./settings"
import General from "./general"
import Company from "./company"
import Files from "pages/common/files"
import Transfer from "./transfer"
import Password from "./password"

export default function Distributor(){

    const toast = useToast()
    const params = useParams()
    const distributor = useDistributor(params.id)
    const user = useUser()
    const block = useDisclosure()
    const edit = useDisclosure()
    const company = useDisclosure()
    const transfer = useDisclosure()
    const password = useDisclosure()

    const handleLogoutDistributor = async () => {
        if(!window.confirm('Tem certeza que deseja deslogar todas as sessões deste núcleo?')) return

        try {
            // Ajuste a rota conforme o seu padrão de API (ex: /distributors/:id/logout)
            await API.post(`/sessions/distributor/${distributor.data?.id}/logout`)
            toast({ title: 'Sucesso', description: 'Sessões do núcleo encerradas.', status: 'success' })
        } catch(err) {
            toast({
                title: 'Erro ao deslogar',
                description: err?.response?.data?.message || 'Erro interno',
                status: 'warning'
            })
        }
    }

    
    return(
    <>
    <Container>

        <Grid templateColumns="50% 50%" alignItems="center">
            
            {/* Breadcrumbs */}
            <GridItem><Breadcrumb prev={{ path: '/distributors', label: 'Núcleos de Distribuição' }} current="Distribuidor" /></GridItem>

            {/* Actions */}
            <GridItem display="flex" justifyContent="flex-end">
                <ButtonGroup size="xs" isDisabled={distributor.isFetching}>
                    <IconButton label="Atualizar" icon="refresh" isLoading={distributor.isFetching} onClick={distributor.refetch} />
                    
                    <Scope role="distributors_update">
                        <IconButton label="Bloquear/Desbloquear" colorScheme="orange" icon="block" onClick={block.onOpen} />
                        <Button leftIcon={<Icon name="edit" />} onClick={edit.onOpen} children="Editar" />
                        <Menu options={[
                            user.data?.roles?.includes('password_distributor') ? {
                                children: 'Alterar senha',
                                icon: 'password',
                                onClick: password.onOpen
                            } : null,
                            {
                                children: 'Atualização fiscal',
                                icon: 'business',
                                onClick: company.onOpen
                            },
                            {
                                children: 'Transferir núcleo',
                                icon: 'transfer',
                                onClick: transfer.onOpen,
                                isDisabled: !Functions.hasRole(user, 'distributors_transfer')
                            },
                            user.data?.roles?.includes('logout_distributor') ? {
                                children: 'Deslogar núcleo',
                                icon: 'logout',
                                onClick: handleLogoutDistributor
                            } : null
                        ].filter(Boolean)} />
                    </Scope>

                    <Dev data={distributor.data} />
                </ButtonGroup>
            </GridItem>

        </Grid>

        <Divider my={4} />

        <Grid templateColumns="20% 80%" gap={6}>

            <GridItem>
                <Stack spacing={4}>

                    <Flex direction="column" alignItems="center" justifyContent="center">
                        <Avatar size="xl" name={distributor.data?.name} mb={2} />
                        <Text textAlign="center" textTransform="capitalize" fontSize="md" children={distributor.data?.name} />
                        <Text fontSize="small" color="GrayText" children={`Aberto ${Moment(distributor?.data?.created_at).fromNow()}`} />
                    </Flex>

                    <Divider />

                    <Label label="Razão social:" children={distributor.data?.company_name} />
                    <Label label="CNPJ:" children={<Clipboard children={Mask.cnpj(distributor.data?.doc_cnpj)} />} />
                    <Label label="Inscrição Estadual:" children={distributor.data?.doc_ie ? <Clipboard children={distributor.data?.doc_ie} /> : <Dash />} fontSize="sm" />

                    <Divider />

                    <Label label="Local:" children={`${distributor.data?.city?.name}, ${distributor.data?.city.state_code}`} />
                    <Label label="Endereço:" children={`${distributor.data?.address_line_1} | ${distributor.data?.address_line_2 ?? ''} ${distributor.data?.address_number} ${distributor.data?.address_neighborhood} | ${distributor.data?.address_complement} ${distributor.data?.zip}`} fontSize="sm" />

                    <Divider />

                    <Label label="E-mail:" children={distributor.data?.email} />
                    <Label label="Telefone do responsável:" children={distributor.data?.mobile} />
                    <Label label="Telefone de atendimento:" children={distributor.data?.work_number} />

                    <Divider />

                    <Label label="Responsável:" children={<Link to={`/consultants/${distributor.data?.customer?.consultant?.id}`} children={distributor.data?.customer?.name} />} />

                    <Divider />

                    <Label label="ID do Núcleo:" children={<Clipboard children={distributor.data?.id} />} fontSize="small" />
                    <Label label="ID do Núcleo (Legado):" children={<Clipboard children={distributor.data?.cod} />} fontSize="small" />

                </Stack>
            </GridItem>

            <GridItem>

                <Stack mb={4}>
                    { !!distributor.data?.blocked_at && <Alert status="error" title="Núcleo bloqueado">Este núcleo foi bloqueado em {Moment(distributor.data?.blocked_at).calendar()} pelo motivo: "{distributor.data?.blocked_reason}".</Alert> }
                    { distributor.isFetched && (!distributor.data?.doc_cnpj || !distributor.data?.doc_ie) && <Alert status="error" title="Notas fiscais indisponíveis" children="Este núcleo não possui um CNPJ ou Inscrição Estadual válidas cadastradas. Contudo não será possível gerar notas fiscais para ele." /> }
                </Stack>

                <Tabs isLazy size="sm" variant="enclosed" colorScheme="orange">
                    <TabList>
                        <Tab>Visão Geral</Tab>

                        <Scope role="distributors_view">
                            <Tab>Estoque</Tab>
                            { Functions.hasRole(user, 'orders_view') && <Tab>Pedidos</Tab> }
                            { Functions.hasRole(user, 'orders_view') && <Tab>Compras</Tab> }
                            <Tab>Extrato</Tab>
                            <Tab>Arquivos</Tab>
                            <Scope role="distributors_options"><Tab>Configurações</Tab></Scope>
                        </Scope>
                    </TabList>

                    <TabPanels>
                        <TabPanel><General data={distributor} /></TabPanel>
                        <TabPanel><Stock data={distributor.data} /></TabPanel>
                        { Functions.hasRole(user, 'orders_view') && <TabPanel><Orders /></TabPanel> }
                        { Functions.hasRole(user, 'orders_view') &&  <TabPanel><Shopping /></TabPanel> }
                        <TabPanel><Extract data={distributor} /></TabPanel>
                        <TabPanel><Files data={distributor.data} model="distributors" title="Arquivos do núcleo" options={{ distributor_id: distributor?.data?.id }} /></TabPanel>
                        <Scope role="distributors_options"><TabPanel><Settings data={distributor} /></TabPanel></Scope>
                    </TabPanels>
                </Tabs>

            </GridItem>
        </Grid>

    </Container>

    {/* Screens */}
    <Block {...block} data={distributor.data} onSuccess={distributor.refetch} />
    <Edit {...edit} data={distributor} /> 
    <Company {...company} data={distributor} />
    <Transfer {...transfer} data={distributor} />
    <Password {...password} data={distributor.data} />

    </>
    )
}